import React, { useState, useMemo } from "react";
import { useT } from "@transifex/react";
import { useQuestionnaire } from "../../../context/questionnaire";
import {
  ContentCard,
  NavButton,
} from "../../../layouts/questionnaire-layout/styles";
import { resolveLandingConfig } from "../../../helpers/questionnaire-helpers";
import styled from "styled-components";

const LandingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SplashImage = styled.img`
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 16px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 1rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 24px;
  text-align: center;
`;

const PrivacyPolicyContainer = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  max-height: 200px;
  overflow-y: auto;

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    margin: 0 0 12px 0;
  }

  p {
    font-size: 13px;
    color: #64748b;
    line-height: 1.6;
    margin: 0;
  }

  a {
    color: #0066cc;
    text-decoration: underline;
    &:hover {
      color: #0052a3;
    }
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
  cursor: pointer;
  font-size: 14px;
  color: #475569;
  line-height: 1.5;

  input {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    accent-color: #0066cc;
    flex-shrink: 0;
    cursor: pointer;
  }

  a {
    color: #0066cc;
    text-decoration: underline;
    &:hover {
      color: #0052a3;
    }
  }
`;

const StartButton = styled(NavButton)`
  width: 100%;
  padding: 16px 24px;
  font-size: 18px;
  min-height: 56px;
`;

const StartPage: React.FC = () => {
  const t = useT();
  const { questionnaire, goToPage } = useQuestionnaire();
  const [accepted, setAccepted] = useState(false);

  const config = useMemo(
    () => resolveLandingConfig(questionnaire?.data),
    [questionnaire?.data]
  );

  if (!questionnaire) return null;

  const title = config.splashTitle || questionnaire.title;

  return (
    <LandingWrapper>
      <ContentCard>
        {config.splashImage && <SplashImage src={config.splashImage} alt="" />}
        <Title>{title}</Title>
        {config.surveyDescription && (
          <Description>{config.surveyDescription}</Description>
        )}
        <CheckboxRow>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span>
            {t("I have read and agree to the")}{" "}
            <a
              href={config.privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {t("Privacy Policy")}
            </a>
            . {t("I understand how my information will be collected and used.")}
          </span>
        </CheckboxRow>
        <StartButton
          variant="primary"
          disabled={!accepted}
          onClick={() => goToPage(0)}
        >
          {config.startButtonTitle || t("Begin")}
        </StartButton>
      </ContentCard>
    </LandingWrapper>
  );
};

export default StartPage;
